import React, { Component } from 'react';
import axios from 'axios';
import { IMaskInput } from 'react-imask';
import { CheckmarkIcon, ErrorIcon, CloseIcon, DocumentIcon } from './SVGIcons';

class OffcanvasForm extends Component {
  static defaultProps = {
    show: false,
    backdrop: true,
    keyboard: true,
    productName: '', // Название товара (передается извне)
    formTitle: 'Заявка', // Заголовок формы по умолчанию
    formText: 'Укажите контактные данные и количество — подготовим КП по выбранному товару. Если нужна доставка, добавьте адрес: рассчитаем условия и сроки под ваш регион.',
    successMessage: 'Сообщение отправлено, наш менеджер свяжется с вами в ближайшее время.',
    errorMessage: 'Произошла ошибка при отправке. Пожалуйста, попробуйте позже.'
  };

  state = {
    formData: {
      productName: this.props.productName || '',
      quantity: '',
      contactPerson: '',
      phone: '',
      city: '',
      addres: '',
      zipCode: '',
      apartment: '',
      agreement: true
    },
    errors: {},
    isSending: false,
    notification: {
      show: false,
      type: 'success', // 'success' или 'error'
      message: ''
    },
    // Состояния для floating labels
    focusedFields: {
      quantity: false,
      contactPerson: false,
      phone: false,
      city: false,
      addres: false,
      zipCode: false,
      apartment: false
    }
  };

  componentDidMount() {
    if (this.props.show) {
      this.handleBodyScrollLock(true);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.show !== prevProps.show) {
      this.handleBodyScrollLock(this.props.show);
      
      // Обновляем productName если он изменился извне
      if (this.props.productName !== prevProps.productName) {
        this.setState(prevState => ({
          formData: {
            ...prevState.formData,
            productName: this.props.productName || ''
          }
        }));
      }
      
      // Сбрасываем форму при открытии
      if (this.props.show && !prevProps.show) {
        this.setState({
          formData: {
            productName: this.props.productName || '',
            quantity: '',
            contactPerson: '',
            phone: '',
            city: '',
            addres: '',
            zipCode: '',
            apartment: '',
            agreement: true
          },
          errors: {},
          notification: {
            show: false,
            type: 'success',
            message: ''
          },
          focusedFields: {
            quantity: false,
            contactPerson: false,
            phone: false,
            city: false,
            addres: false,
            zipCode: false,
            apartment: false
          }
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.props.show) {
      this.handleBodyScrollLock(false);
    }
    
    // Очищаем таймаут если он есть
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  }

  handleBodyScrollLock = (lock) => {
    if (lock) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && this.props.backdrop) {
      this.props.hide();
    }
  };

  handleKeyDown = (e) => {
    if (this.props.keyboard && e.key === 'Escape' && this.props.show) {
      this.props.hide();
    }
  };

  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: type === 'checkbox' ? checked : value
      },
      errors: {
        ...prevState.errors,
        [name]: '' // очищаем ошибку при изменении поля
      }
    }));
  };

  // Обработчик для маски телефона
  handlePhoneChange = (value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        phone: value
      },
      errors: {
        ...prevState.errors,
        phone: ''
      }
    }));
  };

  // Обработчики фокуса для floating label
  handleFocus = (fieldName) => {
    this.setState(prevState => ({
      focusedFields: {
        ...prevState.focusedFields,
        [fieldName]: true
      }
    }));
  };

  handleBlur = (fieldName) => {
    this.setState(prevState => ({
      focusedFields: {
        ...prevState.focusedFields,
        [fieldName]: false
      }
    }));
  };

  // Проверка, нужно ли показывать лейбл сверху
  shouldLabelFloat = (fieldName, value) => {
    const { focusedFields } = this.state;
    return focusedFields[fieldName] || (value && value.toString().length > 0);
  };

  // Показать уведомление об успехе
  showSuccessNotification = (message) => {
    this.setState({
      notification: {
        show: true,
        type: 'success',
        message: message || this.props.successMessage
      }
    });

    // Автоматически скрываем уведомление через 5 секунд
    this.notificationTimeout = setTimeout(() => {
      this.hideNotification();
    }, 5000);
  };

  // Показать уведомление об ошибке
  showErrorNotification = (message) => {
    this.setState({
      notification: {
        show: true,
        type: 'error',
        message: message || this.props.errorMessage
      }
    });

    // Автоматически скрываем уведомление через 5 секунд
    this.notificationTimeout = setTimeout(() => {
      this.hideNotification();
    }, 5000);
  };

  // Скрыть уведомление
  hideNotification = () => {
    this.setState({
      notification: {
        ...this.state.notification,
        show: false
      }
    });
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    // Проверка имени (обязательное)
    if (!formData.contactPerson.trim()) {
      errors.contactPerson = 'Имя обязательно для заполнения';
    }

    // Проверка телефона (обязательное)
    if (!formData.phone.trim()) {
      errors.phone = 'Телефон обязателен';
    } else {
      // Проверяем, что телефон заполнен полностью (не только маска)
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 11) {
        errors.phone = 'Введите полный номер телефона';
      }
    }

    // Проверка количества (обязательное, если есть название товара)
    if (formData.productName !== '' && (!formData.quantity || formData.quantity.trim() === '')) {
      errors.quantity = 'Укажите количество';
    }

    // Проверка согласия
    if (!formData.agreement) {
      errors.agreement = 'Необходимо согласие на обработку данных';
    }

    return errors;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ isSending: true });

    const { formData } = this.state;
    const { formTitle } = this.props;

    // Формируем данные для отправки
    const jsonData = {
      product_name: formData.productName,
      quantity: formData.quantity,
      contact_person: formData.contactPerson,
      phone: formData.phone,
      city: formData.city,
      addres: formData.addres,
      zipCode: formData.zipCode,
      apartment: formData.apartment
    };

    // Удаляем пустые поля из JSON
    Object.keys(jsonData).forEach(key => {
      if (!jsonData[key]) delete jsonData[key];
    });

    axios.post(`${process.env.REACT_APP_API_URL || ''}`, {
      'content_type': 'application/json',
      'action': 'feedback_form',
      'form': formTitle,
      'name': formData.contactPerson,
      'phone': formData.phone,
      'json': JSON.stringify(jsonData)
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Request-Source': 'Node.js'
      }
    }).then((response) => {
      if (response.data.result) {
        // Показываем уведомление об успехе
        this.showSuccessNotification();
        
        // Закрываем форму
        this.props.hide();
      }
    }).catch(error => {
      console.error('Error sending form:', error);
      // Показываем уведомление об ошибке
      this.showErrorNotification();
    }).finally(() => {
      this.setState({ isSending: false });
    });
  };

  // Компонент поля с floating label
  renderFloatingField = (name, label, type = 'text', required = false, maxWidth = '') => {
    const { formData, errors } = this.state;
    const value = formData[name] || '';
    const isFloat = this.shouldLabelFloat(name, value);
    const hasError = errors[name];

    return (
      <div className={`relative ${maxWidth}`}>
        <input
          type={type}
          name={name}
          value={value}
          onChange={this.handleInputChange}
          onFocus={() => this.handleFocus(name)}
          onBlur={() => this.handleBlur(name)}
          className={`w-full h-[54px] px-[15px] pt-[18px] border rounded-[14px] text-[14px] focus:outline-none focus:border-[#032E41] transition-all ${
            hasError ? 'border-red-500' : 'border-gray-200'
          }`}
        />
        <label
          className={`absolute left-[15px] transition-all duration-200 pointer-events-none text-gray-400 ${
            isFloat 
              ? 'top-[6px] text-[10px]' 
              : 'top-1/2 -translate-y-1/2 text-[14px]'
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-[2px]">*</span>}
        </label>
        {hasError && (
          <p className="text-red-500 text-[12px] mt-[5px]">{errors[name]}</p>
        )}
      </div>
    );
  };

  // Поле с маской телефона (floating label)
  renderPhoneField = () => {
    const { formData, errors } = this.state;
    const value = formData.phone || '';
    const isFloat = this.shouldLabelFloat('phone', value);
    const hasError = errors.phone;

    return (
      <div className="relative">
        <IMaskInput
          mask="+7 (000) 000-00-00"
          value={value}
          onAccept={this.handlePhoneChange}
          onFocus={() => this.handleFocus('phone')}
          onBlur={() => this.handleBlur('phone')}
          className={`w-full h-[54px] px-[15px] pt-[18px] border rounded-[14px] text-[14px] focus:outline-none focus:border-[#032E41] transition-all ${
            hasError ? 'border-red-500' : 'border-gray-200'
          }`}
          lazy={true}
        />
        <label
          className={`absolute left-[15px] transition-all duration-200 pointer-events-none text-gray-400 ${
            isFloat 
              ? 'top-[6px] text-[10px]' 
              : 'top-1/2 -translate-y-1/2 text-[14px]'
          }`}
        >
          Телефон <span className="text-red-500">*</span>
        </label>
        {hasError && (
          <p className="text-red-500 text-[12px] mt-[5px]">{errors.phone}</p>
        )}
      </div>
    );
  };

  render() {
    const { show, formTitle, formText, backdrop = true } = this.props;
    const { formData, errors, isSending, notification } = this.state;

    return (
      <>
        {/* Backdrop */}
        {backdrop && (
          <div
            className={`
              fixed inset-0 bg-black/50 backdrop-blur-[4.6px] z-40 
              transition-opacity duration-300 ease-in-out
              ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
            onClick={this.handleBackdropClick}
            onKeyDown={this.handleKeyDown}
            role="presentation"
          />
        )}

        {/* Уведомление */}
        {notification.show && (
          <div
            className={`
              fixed top-5 right-5 z-[60] max-w-[350px] p-4 rounded-lg shadow-lg
              transition-all duration-300 ease-in-out
              ${notification.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}
            `}
          >
            <div className="flex items-start gap-3">
              {/* Иконка */}
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <CheckmarkIcon width={20} height={20} stroke="currentColor" className="text-green-500" />
                ) : (
                  <ErrorIcon width={20} height={20} fill="currentColor" className="text-red-500" />
                )}
              </div>
              
              {/* Текст */}
              <div className="flex-1">
                <p className={`text-sm ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                  {notification.message}
                </p>
              </div>
              
              {/* Кнопка закрытия */}
              <button
                onClick={this.hideNotification}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <CloseIcon width={16} height={16} fill="currentColor" />
              </button>
            </div>
          </div>
        )}

        {/* Offcanvas - справа */}
        <div
          className={`
            fixed right-0 top-0 h-full w-full max-w-[611px] leading-none
            bg-[#F6F7F8] shadow-xl z-50 
            transition-transform duration-300 ease-in-out
            flex flex-col overflow-hidden
            ${show ? 'translate-x-0' : 'translate-x-full'}
          `}
          role="dialog"
          aria-modal="true"
          onKeyDown={this.handleKeyDown}
        >
          {/* Фиксированная шапка */}
          <div className="flex-shrink-0 px-[10px] lg:px-[120px] pt-[20px] lg:pt-[39px]">
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="border-0 bg-white w-[42px] h-[42px] rounded-[14px] flex items-center justify-center hover:bg-gray-200 transition-colors"
                onClick={this.props.hide}
                aria-label="Закрыть"
              >
                <CloseIcon width={16} height={16} fill="currentColor" />
              </button>
            </div>
          </div>

          {/* Прокручиваемая форма */}
          <div className="flex-1 overflow-y-auto px-[10px] lg:px-[40px] pt-[36px] lg:pt-[60px] max-w-[451px] leading-none">
            <div className='text-title text-[#001553] leading-none uppercase'>{formTitle}</div>
            <div className='text-small text-ptgray mt-[12px]'>{formText}</div>
            {formData.productName !== '' && (
              <>
                <div className='text-small text-ptgray mt-[40px] md:mt-[20px]'>Продукция</div>
                <div className='text-cardTitle text-ptblack mt-[12px] max-w-[328px] uppercase'>{formData.productName}</div>
              </>
            )}
            <form onSubmit={this.handleSubmit} className="mt-[30px]">

              {formData.productName !== '' && (
                <>
                  {/* Количество - floating label (обязательное) */}
                  <div className="mt-[20px]">
                    {this.renderFloatingField('quantity', 'Количество', 'text', true, 'md:max-w-[120px]')}
                  </div>
                </>
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-[12px] mt-[12px]'>
                {/* Ваше имя - floating label (обязательное) */}
                {this.renderFloatingField('contactPerson', 'Ваше имя', 'text', true, '')}

                {/* Телефон с маской - floating label (обязательное) */}
                {this.renderPhoneField()}
              </div>

              {formData.productName !== '' && (
                <>
                  {/* Город - floating label */}
                  <div className='mt-[12px]'>
                    {this.renderFloatingField('city', 'Город', 'text', false, '')}
                  </div>

                  {/* Адрес - floating label */}
                  <div className='mt-[12px]'>
                    {this.renderFloatingField('addres', 'Адрес', 'text', false, '')}
                  </div>

                  <div className='grid grid-cols-2 gap-[12px] mt-[12px] md:max-w-[252px]'>
                    {/* Индекс - floating label */}
                    {this.renderFloatingField('zipCode', 'Индекс', 'text', false, '')}

                    {/* Офис/квартира - floating label */}
                    {this.renderFloatingField('apartment', 'Офис/квартира', 'text', false, '')}
                  </div>

                  {/* Текст под полями адреса */}
                  <div className='text-[12px] text-[#001553] mt-[12px]'>
                    Поля адреса доставки необязательны
                  </div>
                </>
              )}

              {/* Галочка согласия */}
              <div className="pt-[10px] mt-[20px]">
                <label className="flex items-start gap-[10px] cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={this.handleInputChange}
                    className="mt-[2px]"
                  />
                  <span className="text-small text-black/60 leading-tight">
                    Я даю согласие на обработку моих персональных данных (фамилия, имя, телефон, адрес электронной почты) в соответствии с <a href="/privacy" target="_blank" className="text-[#032E41] hover:underline">Политикой обработки персональных данных</a> и <a href="/privacy" target="_blank" className="text-[#032E41] hover:underline">Согласием на обработку персональных данных</a>.
                  </span>
                </label>
                {errors.agreement && (
                  <p className="text-red-500 text-small mt-[5px]">{errors.agreement}</p>
                )}
              </div>

              {/* Кнопка отправки */}
              <div className="pt-[25px] pb-[40px]">
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full flex items-center justify-center gap-[12px] max-w-[355px] h-[48px] bg-[#BC0005] duration-300 ease-in-out hover:bg-[#930105] text-white rounded-[14px] text-[16px] font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentIcon width={16} height={16} stroke="currentColor" />
                  <div>{isSending ? 'Отправка...' : 'Отправить'}</div>
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }
}

export default OffcanvasForm;